import clsx from 'clsx';
import Modal from 'components/common/modal';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  CalcTonnage,
  EffectiveTonnage,
  resolveTonnageConflict,
  TonnageRecord,
  tonnageRangeLabel,
  tonnageSourcePhrase,
  tonnageSpan
} from 'utils/tonnage-logic';

// The AHA-6681 tonnage conflict dialog (ruling 5, mock S4) — the arbiter for
// a Layout & Sizing calc that maps outside the current tonnage selection.
// Shared Modal component (NOT sweetalert: the decision is multi-select).
// Hosts: L&S forward-Next (structure page) and the Choose Systems chip's
// passive review affix. Check one card or BOTH (both = contiguous union
// span, source 'union'); confirm resolves through resolveTonnageConflict
// (which stamps resolvedCalcTons so the same calc tile never re-asks);
// cancel/dismiss records nothing — the host stays put and re-asks on the
// next forward attempt.
type TonnageConflictDialogProps = Readonly<{
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  effective: EffectiveTonnage;
  calc: CalcTonnage;
  record: TonnageRecord | null;
  onResolve: (record: TonnageRecord) => void;
}>;

const formatBtu = (btu: number) => Math.round(btu).toLocaleString('en-US');

export default function TonnageConflictDialog({
  openState,
  effective,
  calc,
  record,
  onResolve
}: TonnageConflictDialogProps) {
  const [open] = openState;
  const [keepCurrent, setKeepCurrent] = useState(true);
  const [useCalc, setUseCalc] = useState(false);

  // Fresh defaults every time the dialog opens (a cancel must not leak its
  // half-toggled state into the next ask).
  useEffect(() => {
    if (open) {
      setKeepCurrent(true);
      setUseCalc(false);
    }
  }, [open]);

  const bothChecked = keepCurrent && useCalc;
  const resolved = resolveTonnageConflict({
    effective,
    calc,
    record,
    keepCurrent,
    useCalc
  });
  // Degenerate union (calc sizes inside a span the user kept) renders as the
  // single-value label via tonnageRangeLabel — never "3 – 3".
  const unionLabel = tonnageRangeLabel(
    tonnageSpan(
      Math.min(effective.tons[0], calc.tons[0]),
      Math.max(
        effective.tons[effective.tons.length - 1],
        calc.tons[calc.tons.length - 1]
      )
    )
  );

  const handleConfirm = () => {
    // saveDisabled gates the button, but the Modal's Enter path could still
    // land here — nothing checked means nothing to record.
    if (!resolved) return;
    onResolve(resolved);
  };

  const cardClass = (checked: boolean) =>
    clsx(
      'flex w-full cursor-pointer items-start gap-2 rounded-lg border p-3 text-left text-sm',
      checked
        ? 'border-blue-500 bg-blue-50'
        : 'border-gray-300 bg-white hover:border-gray-400'
    );

  return (
    <Modal
      openState={openState}
      title='Your calculated load doesn’t match your selected size'
      buttonText={bothChecked ? 'Use this size range' : 'Use this size'}
      saveDisabled={!resolved}
      onSave={handleConfirm}
    >
      <div className='flex flex-col gap-3'>
        <p className='text-sm text-gray-600'>
          The Layout &amp; Sizing calculation for this home suggests a different
          size than the one selected on Existing Equipment. Choose which to use
          — you can keep both.
        </p>
        <label className={cardClass(keepCurrent)}>
          <input
            type='checkbox'
            className='mt-0.5'
            checked={keepCurrent}
            onChange={() => setKeepCurrent((prev) => !prev)}
          />
          <span>
            <span className='font-semibold text-gray-900'>
              Your selection — {tonnageRangeLabel(effective.tons)}
            </span>
            <span className='mt-0.5 block text-xs text-gray-500'>
              Picked on the Existing Equipment page (
              {tonnageSourcePhrase(effective.source)}).
            </span>
          </span>
        </label>
        <label className={cardClass(useCalc)}>
          <input
            type='checkbox'
            className='mt-0.5'
            checked={useCalc}
            onChange={() => setUseCalc((prev) => !prev)}
          />
          <span>
            <span className='font-semibold text-gray-900'>
              Calculated requirement — {tonnageRangeLabel(calc.tons)}
            </span>
            <span className='mt-0.5 block text-xs text-gray-500'>
              {formatBtu(calc.btu)} BTU cooling load from your detailed block
              load calculation.
            </span>
          </span>
        </label>
        {/* Union summary — a passive banner, styled nothing like the option
            cards above so it can never read as a third choice (approved
            polish note). */}
        {bothChecked && (
          <div className='rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800'>
            Both selected → systems sized{' '}
            <span className='font-semibold'>{unionLabel}</span> will be shown.
          </div>
        )}
        <div className='flex justify-end'>
          <button
            type='button'
            className='rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50'
            onClick={() => openState[1](false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
